import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BaseApiResponse,
  CdAttachType,
  CdRequestStatus,
  ContractRequestDetailData,
  ContractRequestListItem,
  RejectContractRequest,
  SignContractRequest,
} from "~/types/common";

const inboxKey = (statuses?: CdRequestStatus[]) =>
  ["user-contract-request-inbox", statuses ?? []] as const;
const detailKey = (seq: number) => ["user-contract-request", seq] as const;

/* ─── 받은 요청함 ─── */
const fetchInbox = async (
  statuses?: CdRequestStatus[],
): Promise<ContractRequestListItem[]> => {
  const params = statuses && statuses.length > 0 ? { status: statuses } : {};
  const { data } = await privateHttp.get<
    BaseApiResponse<ContractRequestListItem[]>
  >(API_ENDPOINTS.USER_CONTRACT_REQUEST.INBOX, {
    params,
    paramsSerializer: { indexes: null },
  });
  return data.data ?? [];
};

export const useContractInbox = (statuses?: CdRequestStatus[]) =>
  useQuery({
    queryKey: inboxKey(statuses),
    queryFn: () => fetchInbox(statuses),
  });

/* ─── 상세 (열람 시 자동 OPENED 전환) ─── */
const fetchInboxDetail = async (
  seq: number,
): Promise<ContractRequestDetailData> => {
  const { data } = await privateHttp.get<
    BaseApiResponse<ContractRequestDetailData>
  >(API_ENDPOINTS.USER_CONTRACT_REQUEST.DETAIL(seq));
  return data.data;
};

export const useContractInboxDetail = (seq?: number) =>
  useQuery({
    queryKey: detailKey(seq ?? 0),
    queryFn: () => fetchInboxDetail(seq!),
    enabled: !!seq,
  });

/* ─── 서명 제출 ─── */
export const useSignContract = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: SignContractRequest) => {
      // BE 는 실패도 HTTP200+body.statusCode 로 응답(예: 400 빈 서명) → 코드 검사 필수.
      // 검사 없으면 서명 실패가 성공처럼 처리돼 계약이 OPENED 인데 완료 UI 가 뜬다.
      const { data } = await privateHttp.post<BaseApiResponse<unknown>>(
        API_ENDPOINTS.USER_CONTRACT_REQUEST.SIGN,
        req,
      );
      if (data.statusCode < 200 || data.statusCode >= 300) {
        throw new Error(
          data.message || `서명 제출 실패 (code ${data.statusCode})`,
        );
      }
    },
    onSuccess: (_, req) => {
      qc.invalidateQueries({ queryKey: ["user-contract-request-inbox"] });
      qc.invalidateQueries({ queryKey: detailKey(req.requestSeq) });
    },
  });
};

/* ─── 예약 시점 계약 확보 (find-or-autosend) ─── */
export const useEnsureContractRequest = () =>
  useMutation({
    mutationFn: async (req: {
      companySeq: number;
      petSeq: number;
    }): Promise<number | null> => {
      const { data } = await privateHttp.post<BaseApiResponse<number | null>>(
        API_ENDPOINTS.USER_CONTRACT_REQUEST.ENSURE,
        req,
      );
      // BE 는 실패도 HTTP200+body.statusCode 로 응답 → 코드 검사 필수
      if (data.statusCode < 200 || data.statusCode >= 300) {
        throw new Error(
          data.message || `계약 확보 실패 (code ${data.statusCode})`,
        );
      }
      // data 가 null 이면 계약 불필요(회사에 활성 기본계약서 없음)
      return data.data ?? null;
    },
  });

/* ─── 거절 ─── */
export const useRejectContract = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: RejectContractRequest) => {
      await privateHttp.post(API_ENDPOINTS.USER_CONTRACT_REQUEST.REJECT, req);
    },
    onSuccess: (_, req) => {
      qc.invalidateQueries({ queryKey: ["user-contract-request-inbox"] });
      qc.invalidateQueries({ queryKey: detailKey(req.requestSeq) });
    },
  });
};

/* ─── 첨부 업로드 ─── */
export const useUploadAttachment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      requestSeq: number;
      file: File;
      attachType: CdAttachType;
      note?: string;
    }): Promise<number> => {
      const fd = new FormData();
      fd.append("file", params.file);
      const { data } = await privateHttp.post<BaseApiResponse<number>>(
        API_ENDPOINTS.USER_CONTRACT_REQUEST.ATTACH(params.requestSeq),
        fd,
        {
          params: { attachType: params.attachType, note: params.note },
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return data.data;
    },
    onSuccess: (_, params) => {
      qc.invalidateQueries({ queryKey: detailKey(params.requestSeq) });
    },
  });
};
